import React, { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

function StudentRewards() {
  const [tokenBalance, setTokenBalance] = useState(0);
  const [learningStats, setLearningStats] = useState({
    totalStudyHours: 0,
    assessmentsCompleted: 0,
    sessionsAttended: 0
  });
  const [activeTab, setActiveTab] = useState('rewards');
  

  const userId = localStorage.getItem('userId');

  const fetchStudentData = useCallback(async () => {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setTokenBalance(userData.tokenBalance || 0);
        setLearningStats(userData.learningStats || {
          totalStudyHours: 0,
          assessmentsCompleted: 0,
          sessionsAttended: 0
        });
      }
    } catch (error) {
      console.error('Error fetching student data:', error);
    }
  }, [userId]);

  // const generateTransactionHash = () => {
  //   return '0x' + Math.random().toString(16).substr(2, 64);
  // };

  const purchaseExamRegistration = async (examType) => {
    const examCosts = {
      'waec': 50,
      'neco': 45,
      'jamb': 40
    };

    const cost = examCosts[examType];
    
    if (tokenBalance < cost) {
      alert(`Insufficient tokens. You need ${cost} EDU tokens for ${examType.toUpperCase()} registration.`);
      return;
    }

    try {
      const userRef = doc(db, 'users', userId);
      
      await updateDoc(userRef, {
        tokenBalance: tokenBalance - cost
      });

      alert(`Successfully registered for ${examType.toUpperCase()} using ${cost} EDU tokens!`);
      fetchStudentData();
    } catch (error) {
      console.error('Exam registration error:', error);
      alert('Error processing exam registration');
    }
  };

  const purchasePastQuestions = async (subject, year) => {
    const cost = 5; // 5 tokens per past question set

    if (tokenBalance < cost) {
      alert(`Insufficient tokens. You need ${cost} EDU tokens for past questions.`);
      return;
    }

    try {
      const userRef = doc(db, 'users', userId);
      
      await updateDoc(userRef, {
        tokenBalance: tokenBalance - cost
      });

      alert(`Successfully purchased ${subject} ${year} past questions for ${cost} EDU tokens!`);
      fetchStudentData();
    } catch (error) {
      console.error('Past questions purchase error:', error);
      alert('Error processing purchase');
    }
  };

  useEffect(() => {
    fetchStudentData();
  }, [fetchStudentData]);

  return (
    <div className="card">
      <div className="card-header">
        <h5 className="card-title mb-0">Student Learning Rewards</h5>
      </div>
      <div className="card-body">
        <ul className="nav nav-tabs mb-4">
          <li className="nav-item">
            <button 
              className={`nav-link ${activeTab === 'rewards' ? 'active' : ''}`}
              onClick={() => setActiveTab('rewards')}
            >
              My Rewards
            </button>
          </li>
          <li className="nav-item">
            <button 
              className={`nav-link ${activeTab === 'exams' ? 'active' : ''}`}
              onClick={() => setActiveTab('exams')}
            >
              Exam Registration
            </button>
          </li>
          <li className="nav-item">
            <button 
              className={`nav-link ${activeTab === 'materials' ? 'active' : ''}`}
              onClick={() => setActiveTab('materials')}
            >
              Study Materials
            </button>
          </li>
        </ul>

        {activeTab === 'rewards' && (
          <>
            <div className="row mb-4">
              <div className="col-md-4">
                <div className="border p-3 rounded text-center">
                  <h6>Token Balance</h6>
                  <h3 className="text-warning">{tokenBalance} EDU</h3>
                  <small className="text-muted">Learning Rewards</small>
                </div>
              </div>
              <div className="col-md-4">
                <div className="border p-3 rounded text-center">
                  <h6>Study Hours</h6>
                  <h3 className="text-success">{learningStats.totalStudyHours}h</h3>
                  <small className="text-muted">Total learning</small>
                </div>
              </div>
              <div className="col-md-4">
                <div className="border p-3 rounded text-center">
                  <h6>Assessments</h6>
                  <h3 className="text-primary">{learningStats.assessmentsCompleted}</h3>
                  <small className="text-muted">Completed</small>
                </div>
              </div>
            </div>

            <div className="mt-4">
              <h6>How to Earn Tokens</h6>
              <div className="row">
                <div className="col-md-6">
                  <ul className="list-group">
                    <li className="list-group-item d-flex justify-content-between align-items-center">
                      Complete Assessment
                      <span className="badge bg-warning">+5 EDU</span>
                    </li>
                    <li className="list-group-item d-flex justify-content-between align-items-center">
                      30min Study Session
                      <span className="badge bg-warning">+2 EDU</span>
                    </li>
                    <li className="list-group-item d-flex justify-content-between align-items-center">
                      Attend Tutoring Session
                      <span className="badge bg-warning">+3 EDU</span>
                    </li>
                    <li className="list-group-item d-flex justify-content-between align-items-center">
                      Daily Login
                      <span className="badge bg-warning">+1 EDU</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </>
        )}

        {activeTab === 'exams' && (
          <div>
            <h6>Register for Exams</h6>
            <div className="row">
              <div className="col-md-4 mb-3">
                <div className="card">
                  <div className="card-body text-center">
                    <h5>WAEC</h5>
                    <p>West African Examination Council</p>
                    <div className="mb-3">
                      <strong className="text-warning">50 EDU</strong>
                    </div>
                    <button 
                      className="btn btn-primary"
                      onClick={() => purchaseExamRegistration('waec')}
                    >
                      Register with Tokens
                    </button>
                  </div>
                </div>
              </div>
              <div className="col-md-4 mb-3">
                <div className="card">
                  <div className="card-body text-center">
                    <h5>NECO</h5>
                    <p>National Examination Council</p>
                    <div className="mb-3">
                      <strong className="text-warning">45 EDU</strong>
                    </div>
                    <button 
                      className="btn btn-primary"
                      onClick={() => purchaseExamRegistration('neco')}
                    >
                      Register with Tokens
                    </button>
                  </div>
                </div>
              </div>
              <div className="col-md-4 mb-3">
                <div className="card">
                  <div className="card-body text-center">
                    <h5>JAMB</h5>
                    <p>Joint Admissions & Matriculation Board</p>
                    <div className="mb-3">
                      <strong className="text-warning">40 EDU</strong>
                    </div>
                    <button 
                      className="btn btn-primary"
                      onClick={() => purchaseExamRegistration('jamb')}
                    >
                      Register with Tokens
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'materials' && (
          <div>
            <h6>Past Questions & Answers</h6>
            <p>Purchase past questions using your EDU tokens (5 tokens per subject/year)</p>
            
            <div className="row">
              {['Mathematics', 'English', 'Physics', 'Chemistry', 'Biology'].map(subject => (
                <div key={subject} className="col-md-6 mb-3">
                  <div className="card">
                    <div className="card-body">
                      <h6>{subject}</h6>
                      <div className="row">
                        {[2023, 2022, 2021].map(year => (
                          <div key={year} className="col-4">
                            <button 
                              className="btn btn-outline-primary btn-sm w-100 mb-2"
                              onClick={() => purchasePastQuestions(subject, year)}
                            >
                              {year} (5 EDU)
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default StudentRewards;